import mitt, { Emitter } from 'mitt'
import { Device, CommandResponse } from './api/models'
import { DeviceService } from './api/device.service'
import { webSocketService } from './WebSocketService'
import { isDeviceOnline } from '../utils/device-utils'

export interface DeviceState {
  device: Device | null
  loading: boolean
  error: boolean
  errorMessage: string
  isOnline: boolean
  elapsedSeconds: number | null
  commandStatus: {
    loading: boolean
    status: string
    color: string
    title: string
    resultTitle: string
    message: string
    payload: any
    lastCommand?: {
      deviceId: string
      channel: string
      state?: string
    }
  }
}

export type DeviceStoreEvents = {
  change: DeviceState
  error: Error
}

export class DeviceStore {
  private state: DeviceState
  private emitter: Emitter<DeviceStoreEvents> = mitt<DeviceStoreEvents>()
  private readonly onlineThreshold = 90000
  private clockInterval: any = null

  constructor() {
    this.state = this.getInitialState()

    // Subscribe to WS events if available
    webSocketService.on('messageReceived', this.handleWsMessage.bind(this))
  }

  private getInitialState(): DeviceState {
    return {
      device: null,
      loading: false,
      error: false,
      errorMessage: '',
      isOnline: false,
      elapsedSeconds: null,
      commandStatus: {
        loading: false,
        status: '',
        color: 'primary',
        title: '',
        resultTitle: '',
        message: '',
        payload: null,
      },
    }
  }

  subscribe(handler: (state: DeviceState) => void): () => void {
    this.emitter.on('change', handler)
    // Emit immediate current state
    handler(this.state)
    return () => this.emitter.off('change', handler)
  }

  getState(): DeviceState {
    return this.state
  }

  private setState(partialState: Partial<DeviceState>) {
    this.state = { ...this.state, ...partialState }
    this.emitter.emit('change', this.state)
  }

  private getConnectivityState(device: Device) {
    const lastConn = device.lastConnection
      ? new Date(device.lastConnection).getTime()
      : null
    const isOnline = lastConn
      ? Date.now() - lastConn < this.onlineThreshold
      : false
    const elapsedSeconds = lastConn
      ? Math.floor((Date.now() - lastConn) / 1000)
      : null

    return {
      isOnline,
      elapsedSeconds: isOnline ? elapsedSeconds : null,
    }
  }

  private applyFetchedDevice(device: Device) {
    const connectivity = this.getConnectivityState(device)

    this.setState({
      device,
      loading: false,
      ...connectivity,
    })
  }

  private async syncDevice(
    deviceId: string,
    showLoading = false,
  ): Promise<void> {
    if (showLoading) {
      this.setState({ loading: true, error: false, errorMessage: '' })
    }

    try {
      const device = await DeviceService.getDeviceById(deviceId)
      this.applyFetchedDevice(device)
      this.startClock()
    } catch (error: any) {
      if (showLoading) {
        this.setState({
          loading: false,
          error: true,
          errorMessage: error.message || 'Error al cargar dispositivo',
        })
      }
      throw error
    }
  }

  private startClock() {
    if (this.clockInterval) clearInterval(this.clockInterval)
    this.clockInterval = setInterval(() => this.updateElapsedTime(), 5000)
  }

  private updateElapsedTime() {
    if (!this.state.device) return
    const now = Date.now()
    const lastConn = this.state.device.lastConnection
      ? new Date(this.state.device.lastConnection).getTime()
      : null
    const isOnline = lastConn ? now - lastConn < this.onlineThreshold : false
    const elapsedSeconds = lastConn ? Math.floor((now - lastConn) / 1000) : 0

    if (
      this.state.isOnline !== isOnline ||
      (isOnline && this.state.elapsedSeconds !== elapsedSeconds)
    ) {
      this.setState({
        isOnline,
        elapsedSeconds: isOnline ? elapsedSeconds : null,
      })
    }
  }

  async fetchDevice(deviceId: string): Promise<void> {
    try {
      await this.syncDevice(deviceId, true)
    } catch (error: any) {
      this.emitter.emit('error', error)
      if (this.clockInterval) {
        clearInterval(this.clockInterval)
        this.clockInterval = null
      }
    }
  }

  updateLocalDevice(updates: Partial<Device>) {
    if (this.state.device) {
      this.setState({ device: { ...this.state.device, ...updates } })
    }
  }

  private getPayloadDeviceId(payload: any): string | null {
    if (!payload || typeof payload !== 'object') return null
    if (payload.deviceId) return payload.deviceId
    if (payload.app && payload.city && payload.name) {
      return `${payload.app}/${payload.city}/${payload.name}`
    }
    return null
  }

  private handleWsMessage(event: { type: string; payload: any }) {
    if (!this.state.device) return

    // El payload deviceId devuelto por el bridge es: app/city/name
    const targetId = `${this.state.device.app}/${this.state.device.city}/${this.state.device.name}`
    const payloadDeviceId = this.getPayloadDeviceId(event.payload)

    if (event.type === 'deviceLastConnection' && payloadDeviceId === targetId) {
      const connectionDate =
        event.payload?.date || event.payload?.lastConnection
      const newTimestamp = Date.parse(connectionDate)
      if (!isNaN(newTimestamp)) {
        const updatedDevice = {
          ...this.state.device,
          lastConnection: newTimestamp as any,
        }
        this.setState({ device: updatedDevice })
        this.updateElapsedTime()
      }
    } else if (event.type === 'deviceData' && payloadDeviceId === targetId) {
      this.handleDeviceData(event.payload)
    } else if (event.type === 'deviceInfo') {
      try {
        const infoData = JSON.parse(event.payload.info)
        // Si deviceInfo no contiene deviceId, actualizamos ciegamente si coincide con algún criterio o si solo hay uno
        const updatedDevice = {
          ...this.state.device,
          details: {
            ...this.state.device.details,
            chipId: infoData.id,
            fw_name: infoData.fw_name,
            fw: infoData.fw,
            ip: infoData.ip,
            rssi: infoData.rssi,
          },
        }
        this.setState({ device: updatedDevice })
      } catch (err) {
        console.error('Error parsing deviceInfo in store', err)
      }
    }
  }

  private handleDeviceData(payload: any) {
    if (!this.state.device) return
    const currentData = this.state.device.data || {}
    let updatedChannels = this.state.device.channels

    if (payload.item === 'time_on' && typeof payload.data === 'string') {
      const uptimeValue = parseInt(payload.data, 10)
      if (!Number.isNaN(uptimeValue)) {
        this.setState({
          device: {
            ...this.state.device,
            data: { ...currentData, uptime: uptimeValue } as any,
          },
        })
      }
      return
    }

    let parsed = payload.data
    if (typeof payload.data === 'string') {
      try {
        parsed = JSON.parse(payload.data)
      } catch {
        return
      }
    }
    if (!parsed || typeof parsed !== 'object') return

    const updates: any = {}
    if (parsed.num_resets !== undefined) updates.numResets = parsed.num_resets
    if (parsed.numResets !== undefined) updates.numResets = parsed.numResets
    if (parsed.reset_reason !== undefined)
      updates.resetReason = parsed.reset_reason
    if (parsed.resetReason !== undefined)
      updates.resetReason = parsed.resetReason
    if (parsed.uptime !== undefined) updates.uptime = parsed.uptime
    if (parsed.vin_value !== undefined) {
      updates.vin_value = parsed.vin_value
      updates.powerInValue = parsed.vin_value
    }
    if (parsed.powerInValue !== undefined)
      updates.powerInValue = parsed.powerInValue
    if (parsed.inputs !== undefined) updates.inputs = parsed.inputs
    if (parsed.outputs !== undefined) updates.outputs = parsed.outputs

    if (Object.keys(updates).length === 0) return

    if (updatedChannels && updates.outputs) {
      updatedChannels = updatedChannels.map((ch: any) => {
        if (
          ch &&
          typeof ch.channel === 'string' &&
          Object.prototype.hasOwnProperty.call(updates.outputs, ch.channel)
        ) {
          const val = updates.outputs[ch.channel]
          const isOn = val === 1 || val === true || val === '1' || val === 'ON'
          return { ...ch, lastState: isOn ? 'ON' : 'OFF' }
        }
        return ch
      })
    }

    this.setState({
      device: {
        ...this.state.device,
        data: { ...currentData, ...updates } as any,
        channels: updatedChannels,
      },
    })
  }

  private applyStatePayload(statePayload?: string) {
    if (!this.state.device || !statePayload) return

    try {
      const parsed =
        typeof statePayload === 'string'
          ? JSON.parse(statePayload)
          : statePayload
      if (!parsed || typeof parsed !== 'object') return

      const currentData = this.state.device.data || {}
      const inputs: Record<string, string | number | boolean> = {
        ...(currentData as any).inputs,
      }
      const outputs: Record<string, string | number | boolean> = {
        ...(currentData as any).outputs,
      }
      let updatedChannels = this.state.device.channels || []

      Object.entries(parsed).forEach(([key, value]) => {
        if (key.startsWith('in')) {
          inputs[key] = value as any
          updatedChannels = updatedChannels.map((channel: any) => {
            if (channel?.channel === key) {
              return { ...channel, lastState: String(value) }
            }
            return channel
          })
        }

        if (key.startsWith('out')) {
          outputs[key] = value as any
          updatedChannels = updatedChannels.map((channel: any) => {
            if (channel?.channel === key) {
              return { ...channel, lastState: String(value) }
            }
            return channel
          })
        }
      })

      this.setState({
        device: {
          ...this.state.device,
          data: {
            ...currentData,
            inputs,
            outputs,
          } as any,
          channels: updatedChannels,
        },
      })
    } catch (error) {
      console.error('Error applying state payload in store', error)
    }
  }

  private applyInfoPayload(infoPayload?: string) {
    if (!this.state.device || !infoPayload) return

    try {
      const infoData =
        typeof infoPayload === 'string' ? JSON.parse(infoPayload) : infoPayload
      if (!infoData || typeof infoData !== 'object') return

      const updatedDevice = {
        ...this.state.device,
        details: {
          ...this.state.device.details,
          chipId: infoData.id ?? this.state.device.details?.chipId,
          fw_name: infoData.fw_name ?? this.state.device.details?.fw_name,
          fw: infoData.fw ?? this.state.device.details?.fw,
          ip: infoData.ip ?? this.state.device.details?.ip,
          rssi: infoData.rssi ?? this.state.device.details?.rssi,
        },
      }

      this.setState({ device: updatedDevice })
    } catch (error) {
      console.error('Error applying info payload in store', error)
    }
  }

  private applyDataPayload(dataPayload?: string) {
    if (!this.state.device || !dataPayload) return

    this.handleDeviceData({ data: dataPayload })
  }

  private async syncDeviceAfterCommand(
    deviceId: string,
    channel: string,
    response: CommandResponse,
  ) {
    if (channel === 'all_data' || channel === 'all_info') {
      try {
        await this.syncDevice(deviceId, false)
      } catch (error) {
        console.error(`Error syncing device after command ${channel}:`, error)
      }
    }

    if (channel === 'all_info' && response.info) {
      this.applyInfoPayload(response.info)
    }

    if (channel === 'all_data' && response.data) {
      this.applyDataPayload(response.data)
    }

    if (channel === 'all_state' && response.response) {
      this.applyStatePayload(response.response)
    }
  }

  async sendCommand(
    deviceId: string,
    channel: string,
    state?: string,
  ): Promise<void> {
    const commandTitles: Record<string, string> = {
      'out0': 'Abriendo Puerta (out0)',
      'out1': 'Abriendo Puerta (out1)',
      'pulse_seq': 'Ejecutando Apertura Secuencial',
      'all_state': 'Solicitando Estado Completo',
      'all_data': 'Solicitando Datos Completos',
      'all_info': 'Solicitando Información Completa',
      'reboot': 'Reiniciando Dispositivo',
    }

    this.setState({
      commandStatus: {
        ...this.state.commandStatus,
        loading: true,
        title: commandTitles[channel] || `Enviando comando: ${channel}`,
        message: 'Enviando comando al dispositivo...',
        status: '',
        color: 'primary',
        resultTitle: '',
        payload: null,
        lastCommand: { deviceId, channel, state },
      },
    })

    try {
      const response = await DeviceService.sendCommand(deviceId, channel, state)
      let status,
        color,
        resultTitle,
        message,
        payload = null

      if (
        response.success &&
        (!response.status || response.status === 'success')
      ) {
        status = 'success'
        color = 'success'
        switch (channel) {
          case 'out0':
          case 'out1':
            resultTitle = '¡Puerta abierta!'
            message = `El comando ${channel} se ejecutó correctamente.`
            break
          case 'pulse_seq':
            resultTitle = '¡Secuencia iniciada!'
            message =
              'El dispositivo esta ejecutando la apertura secuencial de ambas salidas.'
            break
          case 'all_state':
          case 'all_data':
          case 'all_info':
            resultTitle = 'Respuesta Recibida'
            message = `Se recibió la respuesta para ${channel}.`
            payload =
              response.response ||
              response.data ||
              response.info ||
              response.state
            break
          case 'reboot':
            resultTitle = 'Comando Enviado'
            message = 'Se envió la orden de reinicio al dispositivo.'
            break
          default:
            resultTitle = 'Éxito'
            message = response.message || 'Comando ejecutado correctamente.'
            payload =
              response.response ||
              response.data ||
              response.info ||
              response.state
        }
      } else if (response.status === 'timeout') {
        status = 'timeout'
        color = 'warning'
        resultTitle = 'Tiempo de espera agotado'
        message = response.message || 'El dispositivo no respondió a tiempo.'
      } else {
        status = 'error'
        color = 'danger'
        resultTitle = 'Error en la ejecución'
        message =
          response.message ||
          `Error desconocido al ejecutar el comando ${channel}.`
      }

      if (status === 'success') {
        await this.syncDeviceAfterCommand(deviceId, channel, response)
      }

      this.setState({
        commandStatus: {
          loading: false,
          status,
          color,
          title: resultTitle,
          resultTitle,
          message,
          payload,
          lastCommand: this.state.commandStatus.lastCommand,
        },
      })
    } catch (error: any) {
      this.setState({
        commandStatus: {
          loading: false,
          status: 'error',
          color: 'danger',
          title: 'Error de Sistema',
          resultTitle: 'Error de Sistema',
          message: `Ha ocurrido un error al intentar enviar el comando: ${error.message}`,
          payload: null,
          lastCommand: this.state.commandStatus.lastCommand,
        },
      })
    }
  }

  reset() {
    this.state = this.getInitialState()
    if (this.clockInterval) {
      clearInterval(this.clockInterval)
      this.clockInterval = null
    }
    this.emitter.emit('change', this.state)
  }
}

export const deviceStore = new DeviceStore()
